<template>
  <div class="admin-user-container">
    <!-- 搜索区域 -->
    <div class="search-bar">
      <div class="search-left">
        <el-button-group>
          <el-button
              :type="searchStatus === null ? 'primary' : ''"
              @click="handleStatusChange(null)"
          >全部</el-button>
          <el-button
              :type="searchStatus === true ? 'primary' : ''"
              @click="handleStatusChange(true)"
          >在线</el-button>
          <el-button
              :type="searchStatus === false ? 'primary' : ''"
              @click="handleStatusChange(false)"
          >未在线</el-button>
        </el-button-group>
      </div>
      <div class="search-right">
        <el-input
            style="width: 230px; "
            v-model="searchText"
            placeholder="请输入用户账号或昵称"
            clearable
            @clear="handleSearch"
            @keyup.enter="handleSearch"
        />

        <el-button
            style="width: 80px; margin-left: 10px;"
            type="primary"
            icon="Search"
            @click="handleSearch"
        >搜索</el-button>
      </div>
    </div>

    <el-table
        :data="userList"
        v-loading="loading"
        class="user-table"
        stripe
        style="width: 100%"
        border
    >
      <el-table-column v-if="false" prop="id" label="ID" width="180" />
      <el-table-column prop="nickname" label="昵称" width="180" />
      <el-table-column prop="username" label="账号" width="180" />
      <el-table-column prop="phoneCredentials" label="手机号" width="180" />

      <el-table-column label="角色" width="160">
        <template #default="scope">
          <el-tag :type="getRoleTagType(scope.row.role)">
            {{ formatRole(scope.row.role) }}
          </el-tag>
        </template>
      </el-table-column>

      <el-table-column prop="loginIp" label="登录IP" width="150" />

      <el-table-column prop="loginBrowser" label="登录方式" width="150">
        <template #default="scope">
          <el-tag>{{ formatLoginBrowser(scope.row.loginBrowser) }}</el-tag>
        </template>
      </el-table-column>

      <el-table-column prop="loginTime" label="登录时间" width="180">
        <template #default="scope">
          {{ formatTime(scope.row.loginTime) }}
        </template>
      </el-table-column>

      <el-table-column label="账号状态" width="106">
        <template #default="scope">
          <el-tag :type="scope.row.status === 1 ? 'success' : 'danger'">
            {{ scope.row.status === 1 ? '正常' : '封禁' }}
          </el-tag>
        </template>
      </el-table-column>

      <!-- 是否在线 -->
      <el-table-column label="是否在线" width="100">
        <template #default="scope">
          <el-tag :type="scope.row.isOnline ? 'success' : 'info'">
            {{ scope.row.isOnline ? '在线' : '未在线' }}
          </el-tag>
        </template>
      </el-table-column>

      <el-table-column label="操作" >
        <template #default="scope">
          <!-- 封禁按钮 -->
          <el-button
              size="small"
              type="danger"
              @click="handleBan(scope.row)"
              v-if="scope.row.status !== 2"
          >封禁</el-button>

          <!-- 解封按钮 -->
          <el-button
              size="small"
              type="success"
              @click="handleUnban(scope.row)"
              v-if="scope.row.status === 2"
          >解封</el-button>

          <!-- 下线按钮 -->
          <el-button
              size="small"
              type="primary"
              @click="handleOffline(scope.row)"
              :disabled="!scope.row.isOnline"
          >下线</el-button>

          <!-- 修改密码按钮 -->
          <el-button
              size="small"
              type="warning"
              @click="handleChangePassword(scope.row)"
          >修改密码</el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页组件 -->
    <div class="pagination-container">
      <el-pagination
          v-model:current-page="currentPage"
          :page-size="pageSize"
          :total="total"
          :pager-count="5"
          layout="prev, pager, next"
          @current-change="handleCurrentChange"
      />
    </div>

    <!-- 修改密码对话框 -->
    <el-dialog
        v-model="changePasswordDialogVisible"
        title="修改密码"
        width="400px"
        :before-close="handleCancelChangePassword"
    >
      <el-form
          :model="changePasswordForm"
          :rules="changePasswordRules"
          ref="changePasswordFormRef"
          label-width="80px"
      >
        <el-form-item label="新密码" prop="newPassword">
          <el-input
              v-model="changePasswordForm.newPassword"
              type="password"
              placeholder="请输入新密码"
              show-password
          />
        </el-form-item>
        <el-form-item label="确认密码" prop="confirmPassword">
          <el-input
              v-model="changePasswordForm.confirmPassword"
              type="password"
              placeholder="请再次输入新密码"
              show-password
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="handleCancelChangePassword">取消</el-button>
          <el-button type="primary" @click="handleConfirmChangePassword" :loading="changePasswordLoading">
            确认修改
          </el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import {ref, onMounted, onUnmounted} from 'vue'
import {ElMessage, ElMessageBox} from 'element-plus'
import request from '@/utils/request'

// 响应式数据
const userList = ref([])
const loading = ref(false)
const searchStatus = ref(null)
const searchText = ref('')

// 修改密码响应式数据
const changePasswordDialogVisible = ref(false)
const changePasswordLoading = ref(false)
const changePasswordFormRef = ref(null)
const currentChangeUser = ref(null)

// 分页相关数据
const currentPage = ref(1)
const pageSize = ref(15)
const total = ref(0)

// 修改密码弹窗表单数据
const changePasswordForm = ref({
  newPassword: '',
  confirmPassword: ''
})

// 密码验证规则
const changePasswordRules = {
  newPassword: [
    { required: true, message: '请输入新密码', trigger: 'blur' },
    { min: 6, message: '密码长度不能少于6位', trigger: 'blur' }
  ],
  confirmPassword: [
    { required: true, message: '请确认密码', trigger: 'blur' },
    {
      validator: (rule, value, callback) => {
        if (value !== changePasswordForm.value.newPassword) {
          callback(new Error('两次输入密码不一致'))
        } else {
          callback()
        }
      },
      trigger: 'blur'
    }
  ]
}

// 获取用户数据
const fetchUserData = async () => {
  try {
    loading.value = true
    const response = await request.get('/user/list')

    if (response.code === '200') {
      userList.value = response.data
      console.log('用户列表数据:', response.data)
    } else {
      ElMessage.error('获取用户数据失败: ' + response.message)
    }
  } catch (error) {
    console.error('请求错误:', error)
    ElMessage.error('获取用户数据失败')
  } finally {
    loading.value = false
  }
}

// 处理状态变化
const handleStatusChange = (status) => {
  searchStatus.value = status
  handleSearch()
}

// 搜索用户
const handleSearch = async () => {
  try {
    loading.value = true
    const params = {
      page: currentPage.value,
      pageSize: pageSize.value
    }
    if (searchStatus.value !== null && searchStatus.value !== '') {
      params.isOnline = searchStatus.value
    }
    if (searchText.value) {
      params.searchText = searchText.value
    }

    const response = await request.get('/user/search-users', {params})

    if (response.code === '200') {
      userList.value = response.data.records || response.data
      total.value = response.data.total || 0
    } else {
      console.error('搜索错误:', response)
    }
  } catch (error) {
    console.error('搜索错误:', error)
  } finally {
    loading.value = false
  }
}

// 强制用户下线
const handleOffline = async (user) => {
  try {
    await ElMessageBox.confirm(
        `确定要强制用户 ${user.username} 下线吗？`,
        '下线确认',
        {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        }
    )

    const response = await request.put(`/user/offline/${user.id}`)

    if (response.code === '200') {
      ElMessage.success('用户已强制下线')
      handleSearch()
    } else {
      ElMessage.error(response.message || '下线操作失败')
    }
  } catch (error) {
    if (error !== 'cancel') {
      console.error('下线失败:', error)
      ElMessage.error('下线操作失败')
    }
  }
}

// 修改密码方法
const handleChangePassword = (user) => {
  currentChangeUser.value = user
  changePasswordForm.value.newPassword = ''
  changePasswordForm.value.confirmPassword = ''
  changePasswordDialogVisible.value = true
}

// 确认修改密码
const handleConfirmChangePassword = async () => {
  if (!changePasswordFormRef.value) return

  try {
    await changePasswordFormRef.value.validate()
    changePasswordLoading.value = true

    const response = await request.put('/user/change-password', {
      userId: currentChangeUser.value.id,
      newPassword: changePasswordForm.value.newPassword
    })

    if (response.code === '200') {
      ElMessage.success('密码修改成功')
      changePasswordDialogVisible.value = false
    } else {
      ElMessage.error(response.message || '密码修改失败')
    }
  } catch (error) {
    if (error !== 'cancel') {
      console.error('修改密码失败:', error)
      ElMessage.error('修改密码失败')
    }
  } finally {
    changePasswordLoading.value = false
  }
}

// 取消修改密码
const handleCancelChangePassword = () => {
  changePasswordDialogVisible.value = false
  if (changePasswordFormRef.value) {
    changePasswordFormRef.value.resetFields()
  }
}

// 封禁用户
const handleBan = async (user) => {
  try {
    await ElMessageBox.confirm(
        `确定要封禁用户 ${user.username} 吗？`,
        '警告',
        {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        }
    )

    await request.put(`/user/ban/${user.id}`)
    ElMessage.success('用户已封禁')
    handleSearch()
  } catch (error) {
    if (error !== 'cancel') {
      console.error('封禁失败:', error)
      ElMessage.error('封禁操作失败')
    }
  }
}

// 解封用户
const handleUnban = async (user) => {
  try {
    await ElMessageBox.confirm(
        `确定要解封用户 ${user.username} 吗？`,
        '确认',
        {
          confirmButtonText: '确定',
          cancelButtonText: '取消'
        }
    )

    const updatedUser = {...user, status: 1}
    await request.put('/user/update', updatedUser)
    ElMessage.success('用户已解封')
    handleSearch()
  } catch (error) {
    if (error !== 'cancel') {
      console.error('解封失败:', error)
      ElMessage.error('解封操作失败')
    }
  }
}

// 格式化登录方式
const formatLoginBrowser = (browser) => {
  if (!browser) return '-'
  if (browser.toLowerCase().includes('chrome')) return 'Chrome'
  if (browser.toLowerCase().includes('firefox')) return 'Firefox'
  if (browser.toLowerCase().includes('safari')) return 'Safari'
  if (browser.toLowerCase().includes('client')) return '客户端'
  return browser
}

// 格式化时间 - 统一格式 YYYY-MM-DD
const formatTime = (time) => {
  if (!time) return '-'
  const date = new Date(time)
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

// 格式化角色显示
const formatRole = (role) => {
  const roleMap = {
    'SUPERADMIN': '超级管理员',
    'NORMALUSER': '普通用户'
  }
  return roleMap[role] || role
}

// 获取角色标签类型
const getRoleTagType = (role) => {
  const tagTypeMap = {
    'SUPERADMIN': 'danger',
    'NORMALUSER': 'info'
  }
  return tagTypeMap[role] || ''
}

// 当前页码改变
const handleCurrentChange = (newPage) => {
  currentPage.value = newPage
  handleSearch()
}

onMounted(async () => {
  handleSearch()
})

onUnmounted(() => {
  changePasswordDialogVisible.value = false;
});
</script>

<style scoped>
.admin-user-container {
  padding: 20px;
  height: 100%;
  overflow: auto;
  box-sizing: border-box;
}

.search-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  gap: 15px;
}

.search-left {
  flex-shrink: 0;
}

.search-right {
  flex: 1;
  max-width: 400px;
}

/* 表格样式 */
.user-table {
  margin-top: 20px;
  border: 1px solid #ebeef5;
  border-radius: 6px;
}

:deep(.el-table__header) {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', 'Helvetica Neue', Helvetica, Arial, sans-serif;
}

:deep(.el-table__header th) {
  font-weight: 600;
  color: #1f2937;
  background-color: #f8fafc !important;
  font-size: 14px;
  letter-spacing: 0.025em;
}

:deep(.el-table__header .cell) {
  font-weight: 600;
  color: #374151;
}

/* 增强表格整体专业感 */
:deep(.el-table) {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', 'Helvetica Neue', Helvetica, Arial, sans-serif;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
}

:deep(.el-table th),
:deep(.el-table td) {
  text-align: center;
  border-bottom: 1px solid #f3f4f6;
}

:deep(.el-table .el-table__row:hover > td) {
  background-color: #f9fafb;
}

/* 分页样式 */
.pagination-container {
  margin-top: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
}

:deep(.el-pagination) {
  font-weight: normal;
}

/* 响应式适配 */
@media (max-width: 768px) {
  .admin-user-container {
    padding: 15px;
  }

  .search-bar {
    flex-direction: column;
    align-items: stretch;
  }

  .search-left,
  .search-right {
    max-width: none;
  }

  .search-left {
    display: flex;
    justify-content: center;
  }
}
</style>